import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiArrowLeft,
  FiArrowRight,
  FiZoomIn,
  FiMinus,
  FiPlus,
  FiShare2,
  FiTruck,
  FiShield,
  FiRefreshCw,
  FiCheck,
} from "react-icons/fi";
import { LuRuler } from "react-icons/lu";

import MainContainer from "../../../components/utils/MainContainer";
import Seo, { seoExcerpt, toAbsoluteUrl } from "../../../components/utils/Seo";
import TShirtSizeGuide from "../../../components/utils/TShirtSizeGuide";
import ProductCard from "../../../components/portal/ProductCard";
import {
  ProductDetailSkeleton,
  ProductGridSkeleton,
} from "../../../components/utils/Skeleton";
import { toast } from "../../../components/modern-ui/sonner";
import { formatPrice } from "../../../utils/utils";
import { FREE_SHIPPING_THRESHOLD_RSD } from "../../../utils/constants";
import { useScrollLock } from "../../../hooks/useScrollLock";
import useReveal from "../../../hooks/useReveal";

import {
  getProduct,
  getSimilarProducts,
  getProductDimensions,
} from "../../../store/slices/products/asyncThunks";
import {
  selectSelectedProduct,
  selectSimilarProducts,
  selectProductDimensions,
} from "../../../store/slices/products/slice";
import { addToCart } from "../../../store/slices/cart/slice";
import { trackPixel, productParams } from "../../../utils/metaPixel";
import { flyToCartFrom } from "../../../components/Header/flyToCart";
import { trackBandView } from "../../../store/slices/viewTracking/asyncThunks";

import {
  Page,
  Breadcrumb,
  MobileBack,
  Layout,
  GalleryCol,
  MainImage,
  ThumbRail,
  Thumb,
  InfoCol,
  BandChip,
  Title,
  PriceBlock,
  PriceRow,
  Price,
  OldPrice,
  DiscountTag,
  Block,
  BlockLabel,
  SizeGuideLink,
  SizeGrid,
  SizeBtn,
  MeasurePill,
  SizeError,
  QuantityControl,
  QtyBtn,
  QtyInput,
  CTARow,
  AddToCartBtn,
  ShareBtn,
  TrustRow,
  TrustItem,
  BelowSection,
  SectionLabel,
  SectionH2,
  Prose,
  SpecDl,
  MoreSection,
  MoreGrid,
  BannerLink,
  ImageModal,
  ModalContent,
  CloseButton,
  StateContainer,
  MobileCTABar,
  MobileCTAPrice,
  MobileCTAButton,
} from "./ProductPageNew.styles";

const SIZE_KEYS = ["XS", "S", "M", "L", "XL", "XXL", "3XL", "4XL", "5XL"];

// Mera sa proizvoda (prečnik, širina, dužina) u tekst bez suvišnih decimala:
// 25 -> "25", 25.5 -> "25.5". Prazno ili besmisleno (0, tekst) -> null.
const formatMeasure = (raw) => {
  if (raw === null || raw === undefined || raw === "") return null;
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
};

export default function ProductPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    data: currentProduct,
    loading,
    error,
  } = useSelector(selectSelectedProduct);

  const similarProducts = useSelector(selectSimilarProducts);
  const productDimensions = useSelector(selectProductDimensions);

  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  // Validacija veličine tek na klik CTA, poruka + shake (v2 s.6)
  const [sizeError, setSizeError] = useState(false);
  const sizesBlockRef = useRef(null);
  const addBtnRef = useRef(null);
  // Mobilna CTA traka, vidljiva samo dok glavno "Dodaj u korpu" dugme
  // nije u vidiku
  const [ctaBarVisible, setCtaBarVisible] = useState(false);

  // Scroll-reveal za sekcije ispod fold-a
  const [belowRef, belowVisible] = useReveal();
  const [moreRef, moreVisible] = useReveal();

  useScrollLock(showImageModal || showSizeGuide);

  useEffect(() => {
    dispatch(getProduct(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (currentProduct?.band?.id) {
      dispatch(trackBandView(currentProduct.band.id));
    }
  }, [currentProduct?.band?.id, dispatch]);

  useEffect(() => {
    if (currentProduct?.id) {
      dispatch(getSimilarProducts(currentProduct.id));
      dispatch(getProductDimensions(currentProduct.id));
      trackPixel("ViewContent", productParams(currentProduct));
    }
  }, [currentProduct?.id, dispatch]);

  useEffect(() => {
    const node = addBtnRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setCtaBarVisible(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [currentProduct?.id]);

  // Reset stanja kada se promeni proizvod
  useEffect(() => {
    setSelectedImage(0);
    setSelectedSize(null);
    setQuantity(1);
    setSizeError(false);
  }, [currentProduct?.id]);

  // Funkcija za formatiranje dimenzija za size guide
  const formatDimensionsForSizeGuide = (dimensions) => {
    if (!dimensions) return null;

    const formattedData = [];
    SIZE_KEYS.forEach((size) => {
      const chest = dimensions[`chest${size}`];
      const length = dimensions[`length${size}`];
      const sleeve = dimensions[`sleeve${size}`];

      if (chest || length || sleeve) {
        formattedData.push({
          size,
          chest: chest ? chest.toString() : "-",
          length: length ? length.toString() : "-",
          sleeve: sleeve ? sleeve.toString() : "-",
        });
      }
    });

    return formattedData.length > 0 ? formattedData : null;
  };

  const productImages = useMemo(() => {
    if (!currentProduct?.productImages) return [];
    const sortedImages = [...currentProduct.productImages].sort(
      (a, b) => a.sortOrder - b.sortOrder,
    );
    return sortedImages.map((img) => img.url);
  }, [currentProduct?.productImages]);

  const discountPercentage = useMemo(() => {
    if (!currentProduct?.price || !currentProduct?.salePrice) return 0;
    return Math.round(
      ((currentProduct.price - currentProduct.salePrice) /
        currentProduct.price) *
        100,
    );
  }, [currentProduct?.price, currentProduct?.salePrice]);

  // Dostupne veličine + stanje po veličini (0 na stanju → precrtano)
  const availableSizes = useMemo(() => {
    if (!currentProduct) return [];
    return SIZE_KEYS.filter((size) => currentProduct[`size${size}`]).map(
      (size) => {
        const rawStock = currentProduct[`stockSize${size}`];
        const outOfStock =
          rawStock !== null && rawStock !== undefined && Number(rawStock) <= 0;
        return { size, outOfStock };
      },
    );
  }, [currentProduct]);

  // Prečnik za okrugle proizvode bez veličina (bedž, prišivak...) — "Ø 25 mm"
  const diameterLabel = useMemo(() => {
    const diameter = formatMeasure(currentProduct?.diameterMm);
    return diameter ? `Ø ${diameter} mm` : null;
  }, [currentProduct?.diameterMm]);

  // Širina i dužina za proizvode bez veličina (ceger, torba...) — "38 × 42 cm".
  // Ako je uneta samo jedna mera, pišemo koja je da ne bude zabune.
  const dimensionsLabel = useMemo(() => {
    const width = formatMeasure(currentProduct?.widthCm);
    const length = formatMeasure(currentProduct?.lengthCm);
    if (width && length) return `${width} × ${length} cm`;
    if (width) return `Širina ${width} cm`;
    if (length) return `Dužina ${length} cm`;
    return null;
  }, [currentProduct?.widthCm, currentProduct?.lengthCm]);

  // Red u specifikaciji: veličine, pa dimenzije, pa prečnik — a ako proizvod
  // nema ništa od toga (šolja, CD) ostaje "Jedna veličina"
  const sizeSpec = useMemo(() => {
    if (availableSizes.length > 0)
      return {
        label: "Dostupne veličine",
        value: availableSizes.map((s) => s.size).join(", "),
      };
    if (dimensionsLabel) return { label: "Dimenzije", value: dimensionsLabel };
    if (diameterLabel) return { label: "Prečnik", value: diameterLabel };
    return { label: "Veličina", value: "Jedna veličina" };
  }, [availableSizes, dimensionsLabel, diameterLabel]);

  const effectiveStock = useMemo(() => {
    if (!currentProduct) return 0;
    return currentProduct.noSize
      ? (currentProduct.stock ?? 0)
      : (currentProduct.stockQuantity ?? 0);
  }, [currentProduct]);

  const handleQuantityChange = (action) => {
    if (action === "increment" && quantity < effectiveStock) {
      setQuantity((prev) => prev + 1);
    } else if (action === "decrement" && quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  const triggerSizeError = () => {
    setSizeError(true);
    const node = sizesBlockRef.current;
    if (node) {
      node.classList.remove("shake");
      // restartuj animaciju
      void node.offsetWidth;
      node.classList.add("shake");
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // Swipe galerije na mobilnom (v2 s.7)
  const touchStartX = useRef(null);
  const onGalleryTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onGalleryTouchEnd = (e) => {
    if (touchStartX.current === null || productImages.length < 2) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 40) return;
    setSelectedImage((prev) =>
      delta < 0
        ? Math.min(productImages.length - 1, prev + 1)
        : Math.max(0, prev - 1),
    );
  };

  const handleAddToCart = (sourceEl) => {
    // Veličina je obavezna ako kategorija ima veličine
    if (currentProduct.category?.hasSizes && availableSizes.length > 0) {
      if (!selectedSize) {
        triggerSizeError();
        return;
      }
    }

    if (effectiveStock <= 0) {
      toast.error("Proizvod trenutno nije dostupan.");
      return;
    }

    if (quantity > effectiveStock) {
      toast.error(`Maksimalna dostupna količina je ${effectiveStock} kom.`);
      return;
    }

    try {
      // Fly animacija mora pre dispatch-a da bi header zamrznuo stari count
      flyToCartFrom(
        sourceEl instanceof HTMLElement ? sourceEl : addBtnRef.current,
      );

      dispatch(
        addToCart({
          product: currentProduct,
          quantity,
          selectedSize: selectedSize,
        }),
      );

      trackPixel("AddToCart", productParams(currentProduct, quantity));

      toast.success(`${currentProduct.name} je uspešno dodat u korpu!`);

      setQuantity(1);
      if (availableSizes.length > 0) {
        setSelectedSize(null);
      }
    } catch (error) {
      toast.error("Došlo je do greške prilikom dodavanja u korpu.");
    }
  };

  const shareProduct = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: currentProduct?.name,
          text: currentProduct?.description,
          url: window.location.href,
        });
      } catch (error) {
        if (error.name !== "AbortError") {
          navigator.clipboard.writeText(window.location.href);
          toast.success("Link je kopiran u clipboard!");
        }
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link je kopiran u clipboard!");
    }
  };

  const handleBack = () => {
    navigate(-1);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  const goTo = (path) => {
    navigate(path);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  if (loading) {
    return (
      <MainContainer>
        <Page>
          <ProductDetailSkeleton />
        </Page>
      </MainContainer>
    );
  }

  if (error || !currentProduct) {
    // Backend vraća 404 i za nepostojeći i za isključen (isActive = false)
    // proizvod — posetiocu je to isto, ne prikazujemo sirovu grešku.
    const isNotFound = /not found|nije pronađen/i.test(String(error || ""));
    return (
      <MainContainer>
        <Page>
          <StateContainer>
            {error && !isNotFound
              ? `Greška prilikom učitavanja proizvoda: ${error}`
              : "Proizvod nije pronađen."}
          </StateContainer>
        </Page>
      </MainContainer>
    );
  }

  const ownerName =
    currentProduct.band?.name || currentProduct.festival?.name || "";
  const ownerPath = currentProduct.band?.id
    ? `/bend/${currentProduct.band.slug || currentProduct.band.id}`
    : currentProduct.festival?.id
      ? `/festival/${currentProduct.festival.slug || currentProduct.festival.id}`
      : null;
  const isFestivalOwner = Boolean(
    !currentProduct.band?.id && currentProduct.festival?.id,
  );

  // Sekcija je "Još od benda/festivala X" → prikazuj samo proizvode istog
  // vlasnika. Backend to već garantuje; ovaj filter štiti od keširanog starog
  // odgovora koji je vraćao proizvode drugih bendova.
  const moreFromOwner = (similarProducts.data || [])
    .filter((product) => product.id !== currentProduct.id)
    .filter((product) =>
      isFestivalOwner
        ? product.festival?.id === currentProduct.festival?.id
        : product.band?.id === currentProduct.band?.id,
    )
    .slice(0, 4);

  // SEO, meta iz podataka proizvoda + Product schema za Google
  const seoDescription = seoExcerpt(currentProduct.description);
  const seoImage = currentProduct.mainImage || productImages[0] || null;
  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: currentProduct.name,
    ...(seoImage ? { image: toAbsoluteUrl(seoImage) } : {}),
    ...(seoDescription ? { description: seoDescription } : {}),
    ...(ownerName ? { brand: { "@type": "Brand", name: ownerName } } : {}),
    offers: {
      "@type": "Offer",
      price: currentProduct.salePrice || currentProduct.price,
      priceCurrency: "RSD",
      availability:
        effectiveStock > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
    },
  };

  return (
    <>
      <MainContainer>
        <Seo
          title={currentProduct.name}
          description={seoDescription}
          canonical={`/proizvod/${currentProduct.slug || currentProduct.id}`}
          image={seoImage}
          type="product"
          jsonLd={productJsonLd}
        />
        <Page>
          {/* Breadcrumb (desktop) / Nazad (mobilno) */}
          <Breadcrumb aria-label="Putanja">
            <Link to="/prodavnica">Prodavnica</Link>
            {ownerName && (
              <>
                <span className="sep">/</span>
                {ownerPath ? (
                  <span className="link" onClick={() => goTo(ownerPath)}>
                    {ownerName}
                  </span>
                ) : (
                  <span className="link">{ownerName}</span>
                )}
              </>
            )}
            <span className="sep">/</span>
            <span className="current">{currentProduct.name}</span>
          </Breadcrumb>
          <MobileBack onClick={handleBack}>
            <FiArrowLeft size={16} /> Nazad
          </MobileBack>

          <Layout>
            {/* Galerija, sticky */}
            <GalleryCol>
              <MainImage
                onClick={() => setShowImageModal(true)}
                onTouchStart={onGalleryTouchStart}
                onTouchEnd={onGalleryTouchEnd}
              >
                <img
                  key={selectedImage}
                  src={productImages[selectedImage] || "/placeholder.jpg"}
                  alt={currentProduct.name}
                  decoding="async"
                  fetchpriority="high"
                />
                <span className="zoom">
                  <FiZoomIn size={18} />
                </span>
              </MainImage>

              {productImages.length > 1 && (
                <ThumbRail>
                  {productImages.map((img, index) => (
                    <Thumb
                      key={index}
                      type="button"
                      $active={selectedImage === index}
                      onClick={() => setSelectedImage(index)}
                    >
                      <img
                        src={img}
                        alt={`${currentProduct.name} ${index + 1}`}
                        loading="lazy"
                        decoding="async"
                      />
                    </Thumb>
                  ))}
                </ThumbRail>
              )}
            </GalleryCol>

            {/* Info kolona */}
            <InfoCol>
              {ownerName && (
                <BandChip onClick={() => ownerPath && goTo(ownerPath)}>
                  {ownerName}
                </BandChip>
              )}

              <Title>{currentProduct.name}</Title>

              <PriceBlock>
                <PriceRow>
                  <Price>
                    {formatPrice(
                      currentProduct.salePrice || currentProduct.price,
                    )}
                  </Price>
                  {currentProduct.salePrice && currentProduct.price && (
                    <>
                      <OldPrice>{formatPrice(currentProduct.price)}</OldPrice>
                      <DiscountTag>-{discountPercentage}%</DiscountTag>
                    </>
                  )}
                </PriceRow>
              </PriceBlock>

              {/* Veličine */}
              {currentProduct.category?.hasSizes &&
                availableSizes.length > 0 && (
                  <Block ref={sizesBlockRef}>
                    <BlockLabel>
                      <span>Veličina</span>
                      {productDimensions.data && (
                        <SizeGuideLink
                          type="button"
                          onClick={() => setShowSizeGuide(true)}
                        >
                          <LuRuler size={14} /> Vodič za veličine
                        </SizeGuideLink>
                      )}
                    </BlockLabel>
                    <SizeGrid>
                      {availableSizes.map(({ size, outOfStock }) => (
                        <SizeBtn
                          key={size}
                          type="button"
                          disabled={outOfStock}
                          $selected={selectedSize === size}
                          onClick={() => {
                            setSelectedSize(size);
                            setSizeError(false);
                          }}
                        >
                          {size}
                        </SizeBtn>
                      ))}
                    </SizeGrid>
                    {sizeError && (
                      <SizeError>
                        Izaberi veličinu pre dodavanja u korpu.
                      </SizeError>
                    )}
                  </Block>
                )}

              {/* Mere: proizvod bez veličina, samo prikaz */}
              {!currentProduct.category?.hasSizes &&
                (dimensionsLabel || diameterLabel) && (
                  <Block>
                    <BlockLabel>
                      <span>{dimensionsLabel ? "Dimenzije" : "Prečnik"}</span>
                    </BlockLabel>
                    <MeasurePill>
                      {dimensionsLabel || diameterLabel}
                    </MeasurePill>
                  </Block>
                )}

              {/* Količina */}
              <Block>
                <BlockLabel>
                  <span>Količina</span>
                </BlockLabel>
                <QuantityControl>
                  <QtyBtn
                    type="button"
                    onClick={() => handleQuantityChange("decrement")}
                    disabled={quantity <= 1}
                  >
                    <FiMinus size={15} />
                  </QtyBtn>
                  <QtyInput
                    type="number"
                    value={quantity}
                    onChange={(e) => {
                      const newQuantity = Math.max(
                        1,
                        Math.min(effectiveStock, parseInt(e.target.value) || 1),
                      );
                      setQuantity(newQuantity);
                    }}
                    min="1"
                    max={effectiveStock}
                  />
                  <QtyBtn
                    type="button"
                    onClick={() => handleQuantityChange("increment")}
                    disabled={quantity >= effectiveStock}
                  >
                    <FiPlus size={15} />
                  </QtyBtn>
                </QuantityControl>
              </Block>

              <CTARow>
                <AddToCartBtn
                  ref={addBtnRef}
                  type="button"
                  onClick={handleAddToCart}
                  disabled={effectiveStock <= 0}
                >
                  {effectiveStock > 0 ? (
                    <>
                      <FiCheck size={18} />
                      Dodaj u korpu
                    </>
                  ) : (
                    "Nema na stanju"
                  )}
                </AddToCartBtn>
                <ShareBtn type="button" onClick={shareProduct} title="Podeli">
                  <FiShare2 size={18} />
                </ShareBtn>
              </CTARow>

              <TrustRow>
                <TrustItem>
                  <FiTruck size={15} />
                  <span>
                    Besplatna dostava preko{" "}
                    {formatPrice(FREE_SHIPPING_THRESHOLD_RSD)}
                  </span>
                </TrustItem>
                <TrustItem>
                  <FiShield size={15} />
                  <span>Garancija kvaliteta</span>
                </TrustItem>
                <TrustItem>
                  <FiRefreshCw size={15} />
                  <span>30 dana za povraćaj</span>
                </TrustItem>
              </TrustRow>
            </InfoCol>
          </Layout>

          {/* O proizvodu + specifikacije, jedan tok, bez tabova */}
          <BelowSection ref={belowRef} $visible={belowVisible}>
            <SectionLabel>Detalji</SectionLabel>
            <SectionH2>O proizvodu</SectionH2>
            <Prose>
              {/* pre-line: novi redovi koje klijent upiše u adminu ostaju novi redovi */}
              <p style={{ whiteSpace: "pre-line" }}>{currentProduct.description}</p>
              <p>
                {isFestivalOwner
                  ? `Ovaj proizvod je deo oficijalne kolekcije festivala ${ownerName}. Dizajn je napravljen u saradnji sa festivalom i predstavlja autentičnu memorabiliju za posetioce.`
                  : `Ovaj proizvod je deo oficijalne kolekcije benda ${ownerName}. Dizajn je napravljen u saradnji sa bendom i predstavlja autentičnu memorabiliju za fanove.`}
              </p>
            </Prose>

            <SpecDl>
              <div>
                <dt>Kategorija</dt>
                <dd>{currentProduct.category?.name || "-"}</dd>
              </div>
              <div>
                <dt>{isFestivalOwner ? "Festival" : "Bend"}</dt>
                <dd>{ownerName || "-"}</dd>
              </div>
              <div>
                <dt>{sizeSpec.label}</dt>
                <dd>{sizeSpec.value}</dd>
              </div>
              {currentProduct?.material && (
                <div>
                  <dt>Materijal</dt>
                  <dd>{currentProduct.material}</dd>
                </div>
              )}
              {currentProduct?.wash && (
                <div>
                  <dt>Održavanje</dt>
                  <dd>{currentProduct.wash}</dd>
                </div>
              )}
            </SpecDl>
          </BelowSection>

          {/* Još od benda, cross-sell */}
          {(similarProducts.loading || moreFromOwner.length > 0) && (
            <MoreSection ref={moreRef} $visible={moreVisible}>
              <SectionLabel>Merch</SectionLabel>
              <SectionH2>
                Još od {isFestivalOwner ? "festivala" : "benda"}{" "}
                {ownerName || ""}
              </SectionH2>
              {similarProducts.loading ? (
                <ProductGridSkeleton count={4} min={230} />
              ) : (
                <>
                  <MoreGrid>
                    {moreFromOwner.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </MoreGrid>
                  {ownerPath && (
                    <BannerLink
                      type="button"
                      onClick={() => goTo(ownerPath)}
                    >
                      Pogledaj {isFestivalOwner ? "festival" : "bend"}{" "}
                      {ownerName}
                      <FiArrowRight size={16} />
                    </BannerLink>
                  )}
                </>
              )}
            </MoreSection>
          )}
        </Page>

        {/* Sticky CTA traka, samo mobilno (cena + Dodaj u korpu) */}
        <MobileCTABar $visible={ctaBarVisible}>
          <MobileCTAPrice>
            <strong>
              {formatPrice(currentProduct.salePrice || currentProduct.price)}
            </strong>
          </MobileCTAPrice>
          <MobileCTAButton
            type="button"
            disabled={effectiveStock <= 0}
            onClick={(e) => handleAddToCart(e.currentTarget)}
          >
            {effectiveStock > 0 ? (
              <>
                <FiCheck size={17} /> Dodaj u korpu
              </>
            ) : (
              "Nema na stanju"
            )}
          </MobileCTAButton>
        </MobileCTABar>
      </MainContainer>

      {/* Image Modal */}
      {showImageModal && (
        <ImageModal onClick={() => setShowImageModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowImageModal(false)}>
              ×
            </CloseButton>
            <img
              src={productImages[selectedImage]}
              alt={currentProduct.name}
              decoding="async"
            />
          </ModalContent>
        </ImageModal>
      )}

      {/* Size Guide Modal */}
      <TShirtSizeGuide
        isOpen={showSizeGuide}
        onClose={() => setShowSizeGuide(false)}
        productDimensions={formatDimensionsForSizeGuide(productDimensions.data)}
      />
    </>
  );
}
